<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import { createRealEstateBrokerInvestmentPresenter } from '../composables/realEstateBrokerInvestmentPresenter';

const props = defineProps({
  item: { type: Object, required: true },
  loading: { type: Boolean, default: false },
  selected: { type: Boolean, default: false },
});

const emit = defineEmits(['select']);
const { t } = useI18n();
const { money, temperatureClass, temperatureIcon, temperatureLabel } =
  createRealEstateBrokerInvestmentPresenter(t);
const percentage = computed(() =>
  Math.max(0, Math.min(100, Number(props.item.porcentagem || 0)))
);
const message = computed(
  () =>
    ({
      frio: t('REAL_ESTATE_BROKER_INVESTMENT.PORTFOLIO.COLD'),
      morno: t('REAL_ESTATE_BROKER_INVESTMENT.PORTFOLIO.WARM'),
    })[props.item.slug] || ''
);
const progressClass = computed(
  () =>
    ({
      frio: 'bg-violet-500',
      morno: 'bg-slate-500',
      quente: 'bg-red-500',
    })[props.item.slug] || 'bg-slate-400'
);
</script>

<template>
  <LaisCard
    density="tight"
    interactive
    selectable
    :selected="selected"
    :aria-label="temperatureLabel(item.slug)"
    :data-testid="`broker-investment-temperature-card-${item.slug}`"
    @click="emit('select', item.slug)"
  >
    <div class="flex min-h-48 flex-col gap-4 p-4">
      <div class="flex items-center justify-between gap-3">
        <span
          class="inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs font-semibold"
          :class="temperatureClass(item.slug)"
        >
          <span
            class="size-3.5"
            :class="temperatureIcon(item.slug)"
            aria-hidden="true"
          />
          {{ temperatureLabel(item.slug) }}
        </span>
        <span class="text-xs font-semibold text-lais-text-muted">
          {{
            t('REAL_ESTATE_BROKER_INVESTMENT.PORTFOLIO.OPTIONS', {
              count: item.quantidade || 0,
            })
          }}
        </span>
      </div>
      <div class="flex items-end gap-3">
        <strong class="text-3xl font-bold text-lais-text">{{
          t('REAL_ESTATE_BROKER_INVESTMENT.SUMMARY.PERCENTAGE', {
            value: percentage.toFixed(0),
          })
        }}</strong>
        <div class="min-w-0 flex-1 pb-1">
          <p class="truncate text-base font-bold text-lais-text">
            {{
              loading
                ? t('REAL_ESTATE_BROKER_INVESTMENT.TABLE.LOADING')
                : money(item.valor)
            }}
          </p>
          <div class="mt-2 h-1.5 overflow-hidden rounded-full bg-lais-bg-soft">
            <span
              class="block h-full rounded-full"
              :class="progressClass"
              :style="{ width: `${percentage}%` }"
            />
          </div>
        </div>
      </div>
      <p
        v-if="message"
        class="border-t border-lais-border-soft pt-3 text-xs text-lais-text-muted"
      >
        {{ message }}
      </p>
    </div>
  </LaisCard>
</template>
